import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import { DateFilter } from "../../components/DateFilter";
import { protocolService } from "../../Services/protocol.service";
import { vehicleService } from "../../Services/vehicle.service";
import { toDateTime } from "../../Services/string.service";
import "./protocols.css";

const EMPTY_FORM = {
  vehicleId: "",
  protocolTypeId: "",
  protocolSubTypeId: "",
  protocolDescription: "",
  sendEmail: "",
};

export default function ProtocolFormModal({ show, protocol, handleClose, onSaved }) {
  const { t } = useTranslation();
  const isEditing = !!protocol;

  const [toastParams, setToastParams] = useState("");
  const showToast = (title, body, color = "Success") =>
    setToastParams(JSON.stringify({ show: true, title, body, color }));

  const [typeOptions, setTypeOptions] = useState([]);
  const [subtypeOptions, setSubtypeOptions] = useState([]);
  const [isLoadingSubtypes, setIsLoadingSubtypes] = useState(false);

  // Modo criação
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isSavingCreate, setIsSavingCreate] = useState(false);

  // Modo detalhes/edição
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedSubtypeId, setSelectedSubtypeId] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [selectedSendEmail, setSelectedSendEmail] = useState("");
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const subtypePreselectedRef = useRef(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatusId, setSelectedStatusId] = useState("");
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const [selectedReminderDate, setSelectedReminderDate] = useState(null);
  const [isSavingReminder, setIsSavingReminder] = useState(false);

  const [interactions, setInteractions] = useState([]);
  const [isLoadingInteractions, setIsLoadingInteractions] = useState(false);

  useEffect(() => {
    if (!show) return;

    setToastParams("");
    protocolService.fetchProtocolTypes().then(setTypeOptions);
    setSubtypeOptions([]);

    if (isEditing) {
      setSelectedStatusId(protocol.protocolStatusId ?? "");
      setSelectedReminderDate(
        protocol.reminderDate ? new Date(protocol.reminderDate) : null
      );
      setSelectedTypeId("");
      setSelectedSubtypeId("");
      setSelectedDescription(protocol.protocolDescription ?? "");
      setSelectedSendEmail(protocol.sendEmail ?? "");
      subtypePreselectedRef.current = false;

      protocolService.fetchProtocolStatusList().then(setStatusOptions);

      setInteractions([]);
      setIsLoadingInteractions(true);
      protocolService
        .fetchProtocolInteractions({
          platformName: protocol.platformName,
          protocolId: protocol.protocolId,
        })
        .then((data) => {
          const sorted = [...data].sort(
            (a, b) => new Date(a.generationDate) - new Date(b.generationDate)
          );
          setInteractions(sorted);
        })
        .finally(() => setIsLoadingInteractions(false));
    } else {
      setCreateForm(EMPTY_FORM);
      setIsLoadingVehicles(true);
      vehicleService
        .fetchVehicles()
        .then((data) => setVehicleOptions(Array.isArray(data) ? data : []))
        .finally(() => setIsLoadingVehicles(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, protocol]);

  // Resolve o tipo atual do protocolo (a listagem só traz o nome, não o id)
  // comparando com as opções de tipo já carregadas.
  useEffect(() => {
    if (!show || !isEditing || selectedTypeId !== "" || typeOptions.length === 0) return;
    const match = typeOptions.find((option) => option.protocolType === protocol.protocolType);
    if (match) setSelectedTypeId(match.protocolTypeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isEditing, typeOptions]);

  useEffect(() => {
    if (!show || !isEditing) return;
    if (selectedTypeId === "") {
      setSubtypeOptions([]);
      return;
    }
    setIsLoadingSubtypes(true);
    protocolService
      .fetchProtocolSubtypes(selectedTypeId)
      .then(setSubtypeOptions)
      .finally(() => setIsLoadingSubtypes(false));
  }, [show, isEditing, selectedTypeId]);

  useEffect(() => {
    if (!show || !isEditing || subtypePreselectedRef.current) return;
    if (subtypeOptions.length === 0) return;
    const match = subtypeOptions.find(
      (option) => option.protocolSubType === protocol.protocolSubtype
    );
    setSelectedSubtypeId(match ? match.protocolSubTypeId : "");
    subtypePreselectedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isEditing, subtypeOptions]);

  const handleEditTypeChange = (e) => {
    setSelectedTypeId(e.target.value);
    setSelectedSubtypeId("");
  };

  const handleCreateFieldChange = (e) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateTypeChange = (e) => {
    const typeId = e.target.value;
    setCreateForm((prev) => ({ ...prev, protocolTypeId: typeId, protocolSubTypeId: "" }));
    setSubtypeOptions([]);

    if (!typeId) return;

    setIsLoadingSubtypes(true);
    protocolService
      .fetchProtocolSubtypes(typeId)
      .then(setSubtypeOptions)
      .finally(() => setIsLoadingSubtypes(false));
  };

  const handleCreateSave = async () => {
    setIsSavingCreate(true);
    try {
      const payload = {
        protocolSubTypeId: Number(createForm.protocolSubTypeId),
        vehicleId: Number(createForm.vehicleId),
        protocolDescription: createForm.protocolDescription,
        sendEmail: createForm.sendEmail,
      };
      const result = await protocolService.createProtocol(payload);
      if (result?.error) {
        showToast(
          t("PROTOCOLS.MODAL.CREATE_TITLE"),
          result.message || t("PROTOCOLS.MODAL.CREATE_ERROR"),
          "Danger"
        );
      } else {
        showToast(t("PROTOCOLS.MODAL.CREATE_TITLE"), t("PROTOCOLS.MODAL.CREATE_SUCCESS"));
        onSaved?.();
        handleClose();
      }
    } finally {
      setIsSavingCreate(false);
    }
  };

  const handleSaveDetails = async () => {
    if (selectedSubtypeId === "" || !protocol) return;

    setIsSavingDetails(true);
    try {
      const success = await protocolService.updateProtocol(protocol.protocolId, {
        protocolSubTypeId: Number(selectedSubtypeId),
        vehicleId: protocol.vehicleId,
        protocolDescription: selectedDescription,
        sendEmail: selectedSendEmail,
      });
      if (success?.error) {
        showToast(
          t("PROTOCOLS.MODAL.DETAILS_TITLE"),
          success.message || t("PROTOCOLS.MODAL.DETAILS_UPDATE_ERROR"),
          "Danger"
        );
      } else {
        showToast(t("PROTOCOLS.MODAL.DETAILS_TITLE"), t("PROTOCOLS.MODAL.DETAILS_UPDATED_SUCCESS"));
        onSaved?.();
      }
    } finally {
      setIsSavingDetails(false);
    }
  };

  const handleSaveStatus = async () => {
    if (selectedStatusId === "") return;

    setIsSavingStatus(true);
    try {
      const success = await protocolService.updateProtocolStatus({
        protocolId: protocol.protocolId,
        platformName: protocol.platformName,
        protocolStatusId: Number(selectedStatusId),
      });
      if (success) {
        showToast(t("PROTOCOLS.MODAL.DETAILS_TITLE"), t("PROTOCOLS.MODAL.STATUS_UPDATED_SUCCESS"));
        onSaved?.();
      } else {
        showToast(t("PROTOCOLS.MODAL.DETAILS_TITLE"), t("PROTOCOLS.MODAL.STATUS_UPDATE_ERROR"), "Danger");
      }
    } finally {
      setIsSavingStatus(false);
    }
  };

  const handleSaveReminder = async () => {
    if (!selectedReminderDate) return;

    setIsSavingReminder(true);
    try {
      const success = await protocolService.updateProtocolReminder({
        protocolId: protocol.protocolId,
        rememberDate: selectedReminderDate.toISOString(),
      });
      if (success) {
        showToast(t("PROTOCOLS.MODAL.DETAILS_TITLE"), t("PROTOCOLS.MODAL.REMINDER_UPDATED_SUCCESS"));
        onSaved?.();
      } else {
        showToast(t("PROTOCOLS.MODAL.DETAILS_TITLE"), t("PROTOCOLS.MODAL.REMINDER_UPDATE_ERROR"), "Danger");
      }
    } finally {
      setIsSavingReminder(false);
    }
  };

  return (
    <>
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? t("PROTOCOLS.MODAL.DETAILS_TITLE") : t("PROTOCOLS.MODAL.CREATE_TITLE")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isEditing ? (
            <>
              <div className="row" style={{ fontSize: 13 }}>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.PROTOCOL_ID")}:</strong> {protocol.protocolId}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.PLATFORM")}:</strong> {protocol.platformName}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.CLIENT")}:</strong> {protocol.clientName}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.REGIONAL")}:</strong> {protocol.regionalName}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.PLATE")}:</strong> {protocol.licensePlate}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.PLATE_ALIAS")}:</strong> {protocol.licensePlateAlias}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.TYPE")}:</strong> {protocol.protocolType}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.EVENT_DATE")}:</strong> {toDateTime(protocol.eventDate)}
                </div>
                <div className="col-md-6 mb-2">
                  <strong>{t("PROTOCOLS.COLUMNS.LAST_UPDATE")}:</strong> {toDateTime(protocol.lastUpdate)}
                </div>
              </div>

              <hr />

              <div className="row align-items-end mb-3">
                <div className="col-md-6">
                  <label style={{ fontSize: 13 }}>{t("PROTOCOLS.MODAL.TYPE_LABEL")}</label>
                  <select
                    className="form-select"
                    value={selectedTypeId}
                    onChange={handleEditTypeChange}
                  >
                    <option value="" disabled>
                      {t("PROTOCOLS.MODAL.TYPE_LABEL")}
                    </option>
                    {typeOptions.map((option) => (
                      <option key={option.protocolTypeId} value={option.protocolTypeId}>
                        {option.protocolType}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label style={{ fontSize: 13 }}>{t("PROTOCOLS.MODAL.SUBTYPE_LABEL")}</label>
                  <select
                    className="form-select protocol-subtype-select"
                    value={selectedSubtypeId}
                    disabled={selectedTypeId === "" || isLoadingSubtypes}
                    onChange={(e) => setSelectedSubtypeId(e.target.value)}
                  >
                    <option value="" disabled>
                      {t("PROTOCOLS.FILTERS.ALL")}
                    </option>
                    {subtypeOptions.map((option) => (
                      <option key={option.protocolSubTypeId} value={option.protocolSubTypeId}>
                        {option.protocolSubType}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <label style={{ fontSize: 13 }}>{t("PROTOCOLS.MODAL.DESCRIPTION_LABEL")}</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={selectedDescription}
                    onChange={(e) => setSelectedDescription(e.target.value)}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label style={{ fontSize: 13 }}>{t("PROTOCOLS.MODAL.EMAIL_LABEL")}</label>
                  <select
                    className="form-select"
                    value={selectedSendEmail}
                    onChange={(e) => setSelectedSendEmail(e.target.value)}
                  >
                    <option value="" disabled>
                      {t("PROTOCOLS.MODAL.EMAIL_LABEL")}
                    </option>
                    <option value="S">{t("COMMON.YES")}</option>
                    <option value="N">{t("COMMON.NO")}</option>
                  </select>
                </div>
              </div>

              <div className="row align-items-end mb-3">
                <div className="col-md-8">
                  <label style={{ fontSize: 13 }}>{t("PROTOCOLS.MODAL.STATUS_LABEL")}</label>
                  <select
                    className="form-select"
                    value={selectedStatusId}
                    onChange={(e) => setSelectedStatusId(e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.protocolStatusId} value={option.protocolStatusId}>
                        {option.protocolStatus}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={isSavingStatus}
                    onClick={handleSaveStatus}
                  >
                    {t("PROTOCOLS.MODAL.SAVE_STATUS_BUTTON")}
                  </Button>
                </div>
              </div>

              <div className="row align-items-end mb-3">
                <div className="col-md-8">
                  <label style={{ fontSize: 13 }}>{t("PROTOCOLS.MODAL.REMINDER_LABEL")}</label>
                  <DateFilter
                    id="protocol-reminder-date"
                    value={selectedReminderDate}
                    handleCallback={setSelectedReminderDate}
                    step={30}
                  />
                </div>
                <div className="col-md-4">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={isSavingReminder}
                    onClick={handleSaveReminder}
                  >
                    {t("PROTOCOLS.MODAL.SAVE_REMINDER_BUTTON")}
                  </Button>
                </div>
              </div>

              <hr />

              <h6>{t("PROTOCOLS.MODAL.INTERACTIONS_TITLE")}</h6>
              {isLoadingInteractions ? (
                <Spinner />
              ) : interactions.length === 0 ? (
                <div className="text-secondary" style={{ fontSize: 13 }}>
                  {t("PROTOCOLS.MODAL.EMPTY_INTERACTIONS")}
                </div>
              ) : (
                <ul className="list-unstyled" style={{ fontSize: 13 }}>
                  {interactions.map((item, index) => (
                    <li
                      key={index}
                      className="py-2"
                      style={{ borderBottom: "1px solid var(--border-color, #e9ecef)" }}
                    >
                      <div className="text-secondary">
                        {toDateTime(item.generationDate)} — {item.userName} ({item.platformName})
                      </div>
                      <div>{item.interaction}</div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="row" style={{ fontSize: 13 }}>
              <div className="col-md-4 mb-3">
                <label>{t("PROTOCOLS.MODAL.VEHICLE_LABEL")}</label>
                <select
                  className="form-select"
                  name="vehicleId"
                  value={createForm.vehicleId}
                  disabled={isLoadingVehicles}
                  onChange={handleCreateFieldChange}
                >
                  <option value="" disabled>
                    {t("PROTOCOLS.MODAL.VEHICLE_LABEL")}
                  </option>
                  {vehicleOptions.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.plate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label>{t("PROTOCOLS.MODAL.TYPE_LABEL")}</label>
                <select
                  className="form-select"
                  name="protocolTypeId"
                  value={createForm.protocolTypeId}
                  onChange={handleCreateTypeChange}
                >
                  <option value="" disabled>
                    {t("PROTOCOLS.MODAL.TYPE_LABEL")}
                  </option>
                  {typeOptions.map((option) => (
                    <option key={option.protocolTypeId} value={option.protocolTypeId}>
                      {option.protocolType}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label>{t("PROTOCOLS.MODAL.SUBTYPE_LABEL")}</label>
                <select
                  className="form-select protocol-subtype-select"
                  name="protocolSubTypeId"
                  value={createForm.protocolSubTypeId}
                  disabled={!createForm.protocolTypeId || isLoadingSubtypes}
                  onChange={handleCreateFieldChange}
                >
                  <option value="" disabled>
                    {t("PROTOCOLS.MODAL.SUBTYPE_LABEL")}
                  </option>
                  {subtypeOptions.map((option) => (
                    <option key={option.protocolSubTypeId} value={option.protocolSubTypeId}>
                      {option.protocolSubType}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 mb-3">
                <label>{t("PROTOCOLS.MODAL.DESCRIPTION_LABEL")}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  name="protocolDescription"
                  value={createForm.protocolDescription}
                  onChange={handleCreateFieldChange}
                />
              </div>
              <div className="col-12 mb-3">
                <label>{t("PROTOCOLS.MODAL.EMAIL_LABEL")}</label>
                <select
                  className="form-select"
                  name="sendEmail"
                  value={createForm.sendEmail}
                  onChange={handleCreateFieldChange}
                >
                  <option value="" disabled>
                    {t("PROTOCOLS.MODAL.EMAIL_LABEL")}
                  </option>
                  <option value="S">{t("COMMON.YES")}</option>
                  <option value="N">{t("COMMON.NO")}</option>
                </select>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isEditing ? (
            <Button
              variant="primary"
              disabled={isSavingDetails || selectedSubtypeId === ""}
              onClick={handleSaveDetails}
            >
              {t("PROTOCOLS.MODAL.SAVE_BUTTON")}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={
                isSavingCreate ||
                !createForm.vehicleId ||
                !createForm.protocolTypeId ||
                !createForm.protocolSubTypeId
              }
              onClick={handleCreateSave}
            >
              {t("PROTOCOLS.MODAL.SAVE_BUTTON")}
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>
            {t("COMMON.CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
